import {
  Dropdown,
  MenuProps,
  message,
  Spin,
  Table,
  TableColumnsType,
  Tabs,
  TabsProps,
} from "antd";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { svg } from "../../../Assets/svg";
import { Link, useNavigate } from "react-router-dom";
import { getAntdSortOrder, useDebounce } from "../../../helpers/utils";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import {
  exportFramePartsAPI,
  getFrameDepthRestrictionsAPI,
  getLengthWidthRestrictionsAPI,
  getprofileHeightRestrctionsAPI,
  getProfileWidthRestrictionsAPI,
} from "./partRestrctionsApis";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import {
  formatFinishLabel,
  ProductFinishEnum,
  ProductFinishLabels,
} from "../Products/productsTypes";

const PartsRestriction = () => {
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.PARTS_RESTRICTION,
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { authLoading } = useAppSelector((state: RootState) => state.loading);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Tabs Constants
  const TAB_HEIGHT = "Uprights Height and Accessories";
  const TAB_DEPTH = "Frame Depth and Bracing Length";
  const TAB_WIDTH = "Uprights Width | Thickness | Max Height | Finish";
  const TAB_LENGTH = "Length & Width";

  const [activeKey, setActiveKey] = useState<string>(TAB_HEIGHT);

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({ field: null, order: null });

  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 300);
  const [loading, setLoading] = useState<boolean>(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(20);

  const [exporting, setExporting] = useState<boolean>(false);
  const messageCloserRef = useRef<any>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (messageCloserRef.current) {
        messageCloserRef.current();
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const renderCardContent = (record: any) => {
    switch (activeKey) {
      case TAB_HEIGHT:
        return (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold fs-5">
                Uprights Height:{" "}
                {record.profile_height_mm
                  ? `${record.profile_height_mm} mm`
                  : "-"}
              </span>
            </div>
            <div className="mobile-card-details fs-6 fs-6-mob">
              <div className="row gy-1">
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Horizontal Bracing:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.horizontal_bracing_qty ?? "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Diagonal Bracing:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.diagonal_bracing_qty ?? "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Nuts & Bolts:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.nuts_and_bolts_qty ?? "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      47mm Spacer (Footplate):
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.spacer_47mm_qty ?? "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      43mm Spacer:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.spacer_43mm_qty ?? "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      case TAB_DEPTH:
        return (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold fs-5">
                Frame Depth:{" "}
                {record.frame_depth_mm ? `${record.frame_depth_mm} mm` : "-"}
              </span>
            </div>
            <div className="mobile-card-details fs-6 fs-6-mob">
              <div className="row gy-1">
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Horizontal Bracing Length:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.horizontal_bracing_length_mm
                        ? `${record.horizontal_bracing_length_mm} mm`
                        : "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Diagonal Bracing Length:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.diagonal_bracing_length_mm
                        ? `${record.diagonal_bracing_length_mm} mm`
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      case TAB_WIDTH:
        const finishDisplay = Array.isArray(record.finish)
          ? record.finish
              .map((f: string) => {
                const finishKey = f as ProductFinishEnum;
                return (
                  ProductFinishLabels[finishKey] || formatFinishLabel(f)
                );
              })
              .join(", ")
          : "-";

        return (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold fs-5">
                Uprights Width:{" "}
                {record.profile_width_mm
                  ? `${record.profile_width_mm} mm`
                  : "-"}
              </span>
            </div>
            <div className="mobile-card-details fs-6 fs-6-mob">
              <div className="row gy-1">
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Thickness:
                    </div>
                    <div className="col-sm-6 col-6">
                      {Array.isArray(record.thickness_mm)
                        ? record.thickness_mm.join(", ")
                        : record.thickness_mm ?? "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      One Piece Max Height:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.one_piece_max_height_mm
                        ? `${record.one_piece_max_height_mm} mm`
                        : "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Finish:
                    </div>
                    <div className="col-sm-6 col-6">{finishDisplay}</div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Nuts & Bolts Size:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.nuts_and_bolts_size ?? "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      case TAB_LENGTH:
        return (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold fs-5">
                Uprights Width:{" "}
                {record.profile_width_mm
                  ? `${record.profile_width_mm} mm`
                  : "-"}
              </span>
            </div>
            <div className="mobile-card-details fs-6 fs-6-mob">
              <div className="row gy-1">
                <div className="col-12">
                  <div className="row">
                    <div className="col-sm-6 col-6 text-secondary fw-medium">
                      Max Uprights Length:
                    </div>
                    <div className="col-sm-6 col-6">
                      {record.max_profile_length_mm
                        ? `${record.max_profile_length_mm} mm`
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // --- 1. DYNAMIC API FETCHING ---
  const fetchData = (page: number, isReset: boolean) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    if (isReset) setLoading(true);

    const filters = {
      page,
      limit,
      search: debouncedSearch,
      sortBy: sorterState.field,
      sortOrder: sorterState.order,
    };

    const commonCallbacks = {
      signal: controller.signal,
      onSuccess: (response: any) => {
        const newData = response?.data || [];
        const total = response?.total || 0;
        setTotalCount(total);

        if (isReset) {
          setData(newData);
          window.scrollTo(0, 0);
        } else {
          setData((prev) => [...prev, ...newData]);
        }

        const currentCount = isReset
          ? newData.length
          : data.length + newData.length;
        setHasMore(currentCount < total);
        setLoading(false);
      },
      onError: () => setLoading(false),
    };

    switch (activeKey) {
      case TAB_HEIGHT:
        dispatch(
          getprofileHeightRestrctionsAPI({ filters, ...commonCallbacks }),
        );
        break;
      case TAB_DEPTH:
        dispatch(getFrameDepthRestrictionsAPI({ filters, ...commonCallbacks }));
        break;
      case TAB_WIDTH:
        dispatch(
          getProfileWidthRestrictionsAPI({ filters, ...commonCallbacks }),
        );
        break;
      case TAB_LENGTH:
        dispatch(
          getLengthWidthRestrictionsAPI({ filters, ...commonCallbacks }),
        );
        break;
      default:
        setLoading(false);
        break;
    }
  };

  // --- 2. EFFECT: Handle Data Loading & Tab Switching ---
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchData(1, true);
  }, [debouncedSearch, sorterState, activeKey]);
  // --- 3. DYNAMIC COLUMNS DEFINITION ---

  const getPlaceholder = () => {
    switch (activeKey) {
      case TAB_HEIGHT:
        return "Search Uprights Height";
      case TAB_DEPTH:
        return "Search Frame Depth";
      case TAB_WIDTH:
        return "Search Uprights Width";
      case TAB_LENGTH:
        return "Search Uprights Width";
      default:
        return "Search";
    }
  };

  const getColumns = (): TableColumnsType<any> => {
    switch (activeKey) {
      case TAB_HEIGHT:
        return [
          {
            title: "Uprights Height (mm)",
            dataIndex: "profile_height_mm",
            sorter: true,
            sortOrder: getAntdSortOrder("profile_height_mm", sorterState),
            render: (val) => val ?? "-",
          },
          {
            title: "Horizontal Bracing (Qty)",
            dataIndex: "horizontal_bracing_qty",
            sorter: true,
            sortOrder: getAntdSortOrder("horizontal_bracing_qty", sorterState),
          },
          {
            title: "Diagonal Bracing (Qty)",
            dataIndex: "diagonal_bracing_qty",
            sorter: true,
            sortOrder: getAntdSortOrder("diagonal_bracing_qty", sorterState),
          },
          {
            title: "Nuts & Bolts (Qty)",
            dataIndex: "nuts_and_bolts_qty",
            sorter: true,
            sortOrder: getAntdSortOrder("nuts_and_bolts_qty", sorterState),
          },
          {
            title: "47mm Spacer (for Footplate)",
            dataIndex: "spacer_47mm_qty",
            sorter: true,
            sortOrder: getAntdSortOrder("spacer_47mm_qty", sorterState),
          },
          {
            title: "43mm Spacer",
            dataIndex: "spacer_43mm_qty",
            sorter: true,
            sortOrder: getAntdSortOrder("spacer_43mm_qty", sorterState),
          },
        ];

      case TAB_DEPTH:
        return [
          {
            title: "Frame Depth (mm)",
            dataIndex: "frame_depth_mm",
            sorter: true,
            sortOrder: getAntdSortOrder("frame_depth_mm", sorterState),
            render: (val) => val ?? "-",
          },
          {
            title: "Horizontal Bracing Length (mm)",
            dataIndex: "horizontal_bracing_length_mm",
            sorter: true,
            sortOrder: getAntdSortOrder(
              "horizontal_bracing_length_mm",
              sorterState,
            ),
          },
          {
            title: "Diagonal Bracing Length (mm)",
            dataIndex: "diagonal_bracing_length_mm",
            sorter: true,
            sortOrder: getAntdSortOrder(
              "diagonal_bracing_length_mm",
              sorterState,
            ),
          },
        ];

      case TAB_WIDTH:
        return [
          {
            title: "Uprights Width (mm)",
            dataIndex: "profile_width_mm",
            sorter: true,
            sortOrder: getAntdSortOrder("profile_width_mm", sorterState),
            render: (val) => val ?? "-",
          },
          {
            title: "Thickness (mm)",
            dataIndex: "thickness_mm",
            sorter: true,
            sortOrder: getAntdSortOrder("thickness_mm", sorterState),
            render: (val) => (Array.isArray(val) ? val.join(", ") : val),
          },
          {
            title: "One Piece Height up to (mm)",
            dataIndex: "one_piece_max_height_mm",
            sorter: true,
            sortOrder: getAntdSortOrder("one_piece_max_height_mm", sorterState),
          },
          {
            title: "Finish",
            dataIndex: "finish",
            sorter: true,
            sortOrder: getAntdSortOrder("finish", sorterState), // ✅ Fixed key
            render: (val: string[]) => {
              if (!Array.isArray(val)) return "-";

              // ✅ Map using ProductFinishLabels for consistent UI
              return val
                .map((f) => {
                  const finishKey = f as ProductFinishEnum;
                  return ProductFinishLabels[finishKey] || formatFinishLabel(f);
                })
                .join(", ");
            },
          },
          {
            title: "Nuts & Bolts Size",
            dataIndex: "nuts_and_bolts_size",
            sorter: true,
            sortOrder: getAntdSortOrder("nuts_and_bolts_size", sorterState),
          },
        ];

      case TAB_LENGTH:
        return [
          {
            title: "Uprights Width (mm)",
            dataIndex: "profile_width_mm",
            sorter: true,
            sortOrder: getAntdSortOrder("profile_width_mm", sorterState),
            render: (val) => val ?? "-",
          },
          {
            title: "Max Uprights Length (mm)",
            dataIndex: "max_profile_length_mm",
            sorter: true,
            sortOrder: getAntdSortOrder("max_profile_length_mm", sorterState),
          },
        ];

      default:
        return [];
    }
  };

  const columns = useMemo(() => getColumns(), [activeKey, sorterState]);

  // Handlers
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const order =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : null;
    setSorterState({ field: sorter.field, order: order as any });
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchData(nextPage, false);
  };

  // ✅ FIX: Set loading true IMMEDIATELY on click
  const onTabChange = (key: string) => {
    setLoading(true);
    setActiveKey(key);
    setSearch("");
    setSorterState({ field: null, order: null });
    setData([]);
  };

  const searchBarHandler = (e: any) => setSearch(e.target.value);
  const searchBarClean = () => setSearch("");

  const handleExport = () => {
    const closeMessage = message.loading("Exporting, please wait...", 0);
    messageCloserRef.current = closeMessage;
    setExporting(true);
    dispatch(
      exportFramePartsAPI({
        onSuccess: () => {
          closeMessage();
          setExporting(false);
          messageCloserRef.current = null;
        },
        onError: () => {
          closeMessage();
          setExporting(false);
          messageCloserRef.current = null;
        },
      }),
    );
  };
  const items: MenuProps["items"] = [
    {
      label: (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (!exporting) handleExport();
          }}
          style={{
            pointerEvents: exporting ? "none" : "auto",
            color: exporting ? "#999" : "inherit",
          }}
        >
          {exporting ? (
            <div className="d-flex align-items-center gap-2">
              <Spin size="small" /> Exporting...
            </div>
          ) : (
            "Export"
          )}
        </a>
      ),
      key: "0",
    },
    ...(USER_PERMISSIONS.ADD || USER_PERMISSIONS.MODIFY
      ? [
          {
            label: <Link to="/parts-restriction/import">Import</Link>,
            key: "1",
          },
        ]
      : []),
  ];

  return USER_PERMISSIONS.READ ? (
    <div className={`page-content h-100 px-40 py-40 d-flex flex-column ${isMobile ? "pb-0" : ""}`}>
      {/* Header Section */}
      <div className="mb-md-3 mb-2 d-flex flex-column gap-md-2 gap-0 flex-shrink-0">
        <div className="d-flex flex-wrap gap-md-3 gap-2 justify-content-between align-items-md-center align-items-start">
          <h2 className="text-black fs-36 fw-bolder page-title mb-0 order-md-1 order-1 w-mob-75">
            Frame Parts, Accessories and Restriction
          </h2>
          <div className="pageSearch mw-100 position-relative d-inline-block ms-auto order-md-2 order-3 mob-w-100">
            <input
              type="text"
              value={search}
              className="form-control form-control-lg"
              onChange={searchBarHandler}
              placeholder={getPlaceholder()}
            />
            {search?.length ? (
              <span className="cursor-pointer" onClick={searchBarClean}>
                {svg?.searchClear}
              </span>
            ) : (
              <span>{svg?.search}</span>
            )}
          </div>
          <div className="d-flex gap-md-3 gap-2 order-md-3 order-2">
            <Dropdown menu={{ items }} trigger={["click"]}>
              <a onClick={(e) => e.preventDefault()} className="d-inline-block">
                <span className="svg-46">{svg?.downloadBtn}</span>
              </a>
            </Dropdown>
            <NotificationButton />
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="flex-shrink-0">
        <div className="customHorizontalTabs bg-white overflow-hidden border-0 rounded-3 mb-md-3 mb-2 d-inline-block mw-100">
          <Tabs
            activeKey={activeKey}
            onChange={onTabChange}
            items={[
              { key: TAB_HEIGHT, label: "Uprights Height and Accessories" },
              { key: TAB_DEPTH, label: "Frame Depth and Bracing Length" },
              {
                key: TAB_WIDTH,
                label: "Uprights Width | Thickness | Max Height | Finish",
              },
              { key: TAB_LENGTH, label: "Length & Width" },
            ]}
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="card page-table-card page-table-device border-0 shadow rounded-4 flex-fill overflow-y-auto" style={{ minHeight: 0 }}>
        <div className="card-body overflow-y-auto" id="scrollableDiv">
          {loading && currentPage === 1 ? (
            <FullScreenLoader />
          ) : data.length === 0 ? (
            <NoDataMessage
              icon={svg?.noPartsRestrictionIcon}
              text="No records found."
            />
          ) : (
            <InfiniteScroll
              dataLength={data.length}
              next={handleLoadMore}
              hasMore={hasMore}
              loader={
                <div className="text-center p-2">
                  <Spin />
                </div>
              }
              scrollableTarget="scrollableDiv"
              height={"100%"}
              style={{ overflow: "auto", paddingBottom: isMobile ? 16 : 0 }}
            >
              {!isMobile ? (
                <Table
                  dataSource={data}
                  columns={columns}
                  onChange={handleTableChange}
                  rowKey={(record) => record._id}
                  pagination={false}
                  className="custom-table"
                  scroll={{ x: "max-content" }}
                  bordered
                />
              ) : (
                <div className="px-3">
                  <div className="row gx-3 gy-3">
                    {data.map((record: any, index: number) => (
                      <div className="col-md-6" key={record._id || index}>
                        <div className="card mobile-table-card mobile-font-s rounded-4 p-3">
                          {renderCardContent(record)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default PartsRestriction;
